import React, { useState, useEffect, useCallback } from 'react';
import { Thunder } from '@ptkl/sdk';

// Thunder SDK integration
const getThunderInstance = () => {
  if (typeof Thunder !== 'undefined') {
    return new Thunder();
  }
  // Fallback for when Thunder is not available
  return null;
};

// Custom UI Components (replacing shadcn)
const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: string; size?: string }> = ({ 
  children, 
  variant = "default", 
  size = "default",
  className = "", 
  ...props 
}) => {
  const baseClass = "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50";
  
  const variantClasses = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    destructive: "bg-red-600 text-white hover:bg-red-700",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
    ghost: "text-gray-700 hover:bg-gray-100",
    link: "text-blue-600 underline-offset-4 hover:underline"
  };
  
  const sizeClasses = {
    default: "h-9 px-4 py-2 text-sm",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9"
  };

  return (
    <button 
      className={`${baseClass} ${variantClasses[variant as keyof typeof variantClasses]} ${sizeClasses[size as keyof typeof sizeClasses]} ${className}`} 
      {...props}
    >
      {children}
    </button>
  );
};

const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = "", ...props }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`} {...props}>
    {children}
  </div>
);

const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = "", ...props }) => (
  <div className={`flex flex-col space-y-1.5 p-6 ${className}`} {...props}>
    {children}
  </div>
);

const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, className = "", ...props }) => (
  <h3 className={`font-semibold leading-none tracking-tight ${className}`} {...props}>
    {children}
  </h3>
);

const CardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ children, className = "", ...props }) => (
  <p className={`text-sm text-gray-600 ${className}`} {...props}>
    {children}
  </p>
);

const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = "", ...props }) => (
  <div className={`p-6 pt-0 ${className}`} {...props}>
    {children}
  </div>
);

const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = "", ...props }) => (
  <div className={`flex items-center p-6 pt-0 ${className}`} {...props}>
    {children}
  </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = "", ...props }) => (
  <input
    className={`flex h-9 w-full rounded-md border border-gray-300 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className = "", ...props }) => (
  <textarea
    className={`flex min-h-[60px] w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Switch: React.FC<{
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}> = ({ checked, onCheckedChange, className = "" }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => onCheckedChange(!checked)}
    className={`peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? 'bg-blue-600' : 'bg-gray-300'
    } ${className}`}
  >
    <span className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform ${
      checked ? 'translate-x-4' : 'translate-x-0'
    }`} />
  </button>
);

const Dialog: React.FC<{ open: boolean; onOpenChange: (open: boolean) => void; children: React.ReactNode }> = ({ 
  open, 
  onOpenChange, 
  children 
}) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/80" 
        onClick={() => onOpenChange(false)}
      />
      <div 
        className="relative z-50 bg-white rounded-lg shadow-lg"
        style={{ width: '400px', maxWidth: '90vw' }}
      >
        {children}
      </div>
    </div>
  );
};

const DialogTrigger: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

const DialogContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`grid gap-4 p-6 ${className}`}>
    {children}
  </div>
);

const DialogHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`flex flex-col space-y-1.5 text-center sm:text-left ${className}`}>
    {children}
  </div>
);

const DialogTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>
    {children}
  </h3>
);

const DialogDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <p className={`text-sm text-gray-600 ${className}`}>
    {children}
  </p>
);

const DialogFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 ${className}`}>
    {children}
  </div>
);

// Lucide Icons replacements
const Upload = ({ className = "" }: { className?: string }) => (
  <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const Trash2 = ({ className = "" }: { className?: string }) => (
  <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const Settings = ({ className = "" }: { className?: string }) => (
  <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const Package = ({ className = "" }: { className?: string }) => (
  <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const Eye = ({ className = "" }: { className?: string }) => (
  <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const Download = ({ className = "" }: { className?: string }) => (
  <svg className={`h-4 w-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// Types
interface ThunderPlugin {
  pluginId: string;
  name: string;
  version: string;
  description?: string;
  author?: string;
  active: boolean;
  url?: string;
  code?: string;
  config?: Record<string, any>;
  createdAt?: Date;
  updatedAt?: Date;
  isLoaded?: boolean;
  activePlugin?: any;
}

interface PluginManagementProps {
  repository?: string;
  className?: string;
  onPluginActivate?: (plugin: ThunderPlugin) => Promise<void> | void;
  onPluginDeactivate?: (plugin: ThunderPlugin) => Promise<void> | void;
  onPluginInstall?: (plugin: ThunderPlugin) => Promise<void> | void;
  onPluginDelete?: (plugin: ThunderPlugin) => Promise<void> | void;
}

// Helper function to extract plugin metadata from code
// Priority: 1. meta object, 2. package.json content, 3. legacy patterns, 4. comments
const extractPluginInfo = (code: string) => {
  const info: Partial<{ id: string; name: string; version: string; description: string; author: string }> = {};
  
  // Priority 1: Look for plugin meta object (most reliable source)
  const metaPatterns = [
    // Specific pattern for built plugins: meta: { name: 'Sattrakt', version: '1.0.0.beta.1' }
    /meta\s*:\s*\{\s*name\s*:\s*['"`]([^'"`]+)['"`]\s*,\s*version\s*:\s*['"`]([^'"`]+)['"`]/s,
    /meta\s*:\s*\{\s*version\s*:\s*['"`]([^'"`]+)['"`]\s*,\s*name\s*:\s*['"`]([^'"`]+)['"`]/s,
    // Standard meta object pattern: meta: { name: "...", version: "..." }
    /meta\s*:\s*\{\s*([^}]*)\s*\}/s,
    // Alternative pattern: .meta = { name: "...", version: "..." }
    /\.meta\s*=\s*\{\s*([^}]*)\s*\}/s,
    // Plugin object with meta: const plugin = { meta: { ... } }
    /(?:const|let|var)\s+\w+\s*=\s*\{[^}]*meta\s*:\s*\{\s*([^}]*)\s*\}/s
  ];
  
  for (const pattern of metaPatterns) {
    const metaMatch = code.match(pattern);
    if (metaMatch) {
      const metaContent = metaMatch[1] || metaMatch[0];
      
      // Handle direct name/version matches from specific patterns
      if (metaMatch.length > 2) {
        // Pattern matched name and version directly
        if (!info.name && metaMatch[1]) info.name = metaMatch[1];
        if (!info.version && metaMatch[2]) info.version = metaMatch[2];
        // Swap if version came first
        if (!info.name && metaMatch[2] && metaMatch[1]?.includes('.')) {
          info.name = metaMatch[2];
          info.version = metaMatch[1];
        }
      } else {
        // Extract from meta content
        if (!info.name) {
          const nameMatch = metaContent.match(/name\s*:\s*['"`]([^'"`]+)['"`]/);
          if (nameMatch) info.name = nameMatch[1];
        }
        
        if (!info.version) {
          const versionMatch = metaContent.match(/version\s*:\s*['"`]([^'"`]+)['"`]/);
          if (versionMatch) info.version = versionMatch[1];
        }
        
        if (!info.description) {
          const descMatch = metaContent.match(/description\s*:\s*['"`]([^'"`]+)['"`]/);
          if (descMatch) info.description = descMatch[1];
        }
        
        if (!info.author) {
          const authorMatch = metaContent.match(/author\s*:\s*['"`]([^'"`]+)['"`]/);
          if (authorMatch) info.author = authorMatch[1];
        }
        
        if (!info.id) {
          const idMatch = metaContent.match(/id\s*:\s*['"`]([^'"`]+)['"`]/);
          if (idMatch) info.id = idMatch[1];
        }
      }
    }
  }
  
  // Priority 2: Look for package.json content embedded in the code
  const packageJsonMatch = code.match(/(?:require\(['"`]\.\/package\.json['"`]\)|import\s+.*?\s+from\s+['"`]\.\/package\.json['"`]|package\s*=\s*\{[\s\S]*?\})/);
  if (packageJsonMatch) {
    // Try to extract package.json-like object
    const packageMatch = code.match(/(?:const|let|var)\s+package(?:Json)?\s*=\s*(\{[\s\S]*?\});/);
    if (packageMatch) {
      const packageContent = packageMatch[1];
      
      // Extract from package.json structure
      if (!info.name) {
        const nameMatch = packageContent.match(/name\s*:\s*['"`]([^'"`]+)['"`]/);
        if (nameMatch) info.name = nameMatch[1];
      }
      
      if (!info.version) {
        const versionMatch = packageContent.match(/version\s*:\s*['"`]([^'"`]+)['"`]/);
        if (versionMatch) info.version = versionMatch[1];
      }
      
      if (!info.description) {
        const descMatch = packageContent.match(/description\s*:\s*['"`]([^'"`]+)['"`]/);
        if (descMatch) info.description = descMatch[1];
      }
      
      if (!info.author) {
        const authorMatch = packageContent.match(/author\s*:\s*['"`]([^'"`]+)['"`]/);
        if (authorMatch) info.author = authorMatch[1];
      }
    }
  }
  
  // Priority 3: Look for legacy plugin registration patterns (fallback)
  if (!info.name || !info.id) {
    const registrationMatch = code.match(/window\.PluginRegistry\.register\s*\(\s*['"](.*?)['"],\s*\{[\s\S]*?name\s*:\s*['"`](.*?)['"`]/);
    if (registrationMatch) {
      if (!info.id) info.id = registrationMatch[1];
      if (!info.name) info.name = registrationMatch[2];
    }
  }
  
  // Priority 4: Look for any version/description patterns in comments or standalone declarations
  if (!info.version) {
    const versionMatch = code.match(/(?:@version\s+([\d.]+)|Version:\s*([\d.]+)|version\s*[:=]\s*['"`]([\d.]+)['"`])/i);
    if (versionMatch) {
      info.version = versionMatch[1] || versionMatch[2] || versionMatch[3];
    }
  }
  
  if (!info.description) {
    const descMatch = code.match(/(?:@description\s+(.+)|Description:\s*(.+)|description\s*[:=]\s*['"`](.+?)['"`])/i);
    if (descMatch) {
      info.description = (descMatch[1] || descMatch[2] || descMatch[3])?.trim();
    }
  }
  
  return info;
};

/**
 * PluginManagement Component
 * 
 * Handles plugin upload, activation, and management through Thunder SDK.
 * Plugin metadata extraction priority:
 * 1. Plugin meta object (meta: { name, version, description, author })
 * 2. Package.json content embedded in code
 * 3. Legacy registration patterns
 * 4. Comments and standalone declarations
 * 5. Filename and defaults as final fallback
 */
export const PluginManagement: React.FC<PluginManagementProps> = ({
  repository = 'plugins',
  className = '',
  onPluginActivate,
  onPluginDeactivate,
  onPluginInstall,
  onPluginDelete
}) => {
  const [thunderPlugins, setThunderPlugins] = useState<ThunderPlugin[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isInstallDialogOpen, setIsInstallDialogOpen] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState<ThunderPlugin | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadThunderPlugins = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const thunder = getThunderInstance();
      if (!thunder) {
        // If Thunder is not available, show empty state
        setThunderPlugins([]);
        setIsLoading(false);
        return;
      }

      // Load plugins from Thunder SDK
      const response = await thunder.find(repository, {});
      const plugins = response?.data || [];

      console.log('Fetched plugins from Thunder SDK:', plugins);
      
      // Convert Thunder plugins to our format
      const formattedPlugins: ThunderPlugin[] = plugins.map((plugin: any) => ({
        pluginId: plugin.pluginId,
        name: plugin.name || 'Unknown Plugin',
        version: plugin.version || '1.0.0',
        description: plugin.description || '',
        author: plugin.author,
        active: plugin.active || false,
        url: plugin.url,
        code: plugin.code,
        config: plugin.config || {},
        createdAt: plugin.createdAt ? new Date(plugin.createdAt) : new Date(),
        updatedAt: plugin.updatedAt ? new Date(plugin.updatedAt) : new Date(),
        isLoaded: plugin.active || false
      }));
      
      setThunderPlugins(formattedPlugins);
    } catch (err) {
      setError('Failed to load plugins from Thunder SDK');
      console.error('Error loading Thunder plugins:', err);
      setThunderPlugins([]);
    } finally {
      setIsLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    loadThunderPlugins();
  }, [loadThunderPlugins]);

  // For demonstration, we'll use an empty array for activePlugins since we're getting real data from Thunder
  const activePlugins: any[] = [];

  // Use Thunder plugins directly instead of merging with mock active plugins
  const mergedPlugins = thunderPlugins.map(plugin => ({
    ...plugin,
    isLoaded: plugin.active // Consider active plugins as loaded
  }));

  const handleUploadPlugin = async () => {
    if (!uploadFile) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const code = await uploadFile.text();
      const pluginInfo = extractPluginInfo(code);
      
      // Log extracted information for debugging
      console.log('📊 Extracted plugin info:', pluginInfo);
      console.log('📝 First 500 chars of code:', code.substring(0, 500));
      console.log('🔍 Search for meta pattern:', code.includes('meta:'));
      
      // Debug: Try to find the specific meta pattern in the code
      const debugMatch = code.match(/meta\s*:\s*\{\s*name\s*:\s*['"`]([^'"`]+)['"`]\s*,\s*version\s*:\s*['"`]([^'"`]+)['"`]/s);
      if (debugMatch) {
        console.log('🎯 Found meta pattern:', debugMatch[0]);
        console.log('🏷️ Name from pattern:', debugMatch[1]);
        console.log('📋 Version from pattern:', debugMatch[2]);
      }
      
      // Better fallback logic for plugin metadata
      const fileName = uploadFile.name.replace(/\.[^/.]+$/, "");
      
      const newPlugin: ThunderPlugin = {
        pluginId: pluginInfo.id || `plugin-${Date.now()}`,
        name: pluginInfo.name || fileName,
        version: pluginInfo.version || '1.0.0',
        description: pluginInfo.description || `Uploaded plugin: ${fileName}`,
        active: false,
        code,
        createdAt: new Date(),
        isLoaded: false,
        // Add author if available
        ...(pluginInfo.author && { author: pluginInfo.author })
      };
      
      console.log('🔌 Created plugin object:', {
        name: newPlugin.name,
        version: newPlugin.version,
        description: newPlugin.description,
        author: newPlugin.author
      });
      
      // Save to Thunder SDK
      const thunder = getThunderInstance();
      if (thunder) {
        try {
          await thunder.upsert(repository, { pluginId: newPlugin.pluginId }, newPlugin);
          console.log(`Plugin ${newPlugin.name} saved to Thunder SDK`);
        } catch (thunderError) {
          console.warn('Failed to save to Thunder SDK, using local state:', thunderError);
        }
      }
      
      // Call the install callback if provided
      if (onPluginInstall) {
        await onPluginInstall(newPlugin);
      }
      
      // Add to local state and reload from Thunder
      await loadThunderPlugins();
      setUploadFile(null);
      setIsInstallDialogOpen(false);
      
      console.log(`Plugin ${newPlugin.name} installed successfully`);
    } catch (err) {
      setError('Failed to upload plugin');
      console.error('Error uploading plugin:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePlugin = async (plugin: ThunderPlugin) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const willBeActive = !plugin.active;
      
      // Update in Thunder SDK first
      const thunder = getThunderInstance();
      if (thunder) {
        try {
          const updatedPlugin = { ...plugin, active: willBeActive };
          await thunder.upsert(repository, { pluginId: plugin.pluginId }, updatedPlugin);
          console.log(`Plugin ${plugin.name} ${willBeActive ? 'activated' : 'deactivated'} in Thunder SDK`);
        } catch (thunderError) {
          console.warn('Failed to update Thunder SDK, using local state:', thunderError);
        }
      }
      
      // Call the appropriate callback
      if (willBeActive && onPluginActivate) {
        await onPluginActivate(plugin);
      } else if (!willBeActive && onPluginDeactivate) {
        await onPluginDeactivate(plugin);
      }
      
      // Reload plugins from Thunder to get fresh state
      await loadThunderPlugins();
      
      console.log(`Plugin ${plugin.name} ${willBeActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      setError(`Failed to ${plugin.active ? 'deactivate' : 'activate'} plugin: ${plugin.name}`);
      console.error('Error toggling plugin:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlugin = async (plugin: ThunderPlugin) => {
    if (!confirm(`Are you sure you want to delete ${plugin.name}?`)) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Delete from Thunder SDK first
      const thunder = getThunderInstance();
      if (thunder) {
        try {
          await thunder.delete(repository, { pluginId: plugin.pluginId });
          console.log(`Plugin ${plugin.name} deleted from Thunder SDK`);
        } catch (thunderError) {
          console.warn('Failed to delete from Thunder SDK:', thunderError);
        }
      }
      
      // Call the delete callback if provided
      if (onPluginDelete) {
        await onPluginDelete(plugin);
      }
      
      // Reload plugins from Thunder to get fresh state
      await loadThunderPlugins();
      
      console.log(`Plugin ${plugin.name} deleted successfully`);
    } catch (err) {
      setError(`Failed to delete plugin: ${plugin.name}`);
      console.error('Error deleting plugin:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const enabledCount = mergedPlugins.filter(p => p.active).length;
  const loadedCount = mergedPlugins.filter(p => p.isLoaded).length;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Package />
          <h2 className="text-2xl font-bold">Plugin Management</h2>
        </div>
        <Button onClick={() => setIsInstallDialogOpen(true)} disabled={isLoading}>
          <Upload />
          <span className="ml-2">Install Plugin</span>
        </Button>
      </div>

      {/* Install Plugin Dialog */}
      <Dialog open={isInstallDialogOpen} onOpenChange={setIsInstallDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Install New Plugin</DialogTitle>
            <DialogDescription>
              Upload a plugin file to install it in your application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Plugin File</label>
              <Input
                type="file"
                accept=".js,.jsx,.ts,.tsx"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-500 mt-2">
                Supported formats: .js, .jsx, .ts, .tsx
              </p>
            </div>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInstallDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadPlugin} 
              disabled={!uploadFile || isLoading}
            >
              {isLoading ? 'Installing...' : 'Install Plugin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setError(null)}
                className="text-red-700 hover:text-red-900"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-4 gap-6 max-w-8xl">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Plugins</CardTitle>
            <Package />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mergedPlugins.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Eye />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{enabledCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Loaded</CardTitle>
            <Settings />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{loadedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive</CardTitle>
            <Settings />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{mergedPlugins.length - enabledCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Plugin List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Installed Plugins</h3>
        {isLoading && mergedPlugins.length === 0 ? (
          <Card>
            <CardContent>
              <div className="text-center text-gray-500 py-8">
                <p>Loading plugins...</p>
              </div>
            </CardContent>
          </Card>
        ) : mergedPlugins.length === 0 ? (
          <Card>
            <CardContent>
              <div className="text-center text-gray-500 py-8">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">No plugins installed</p>
                <p className="text-sm">Get started by installing your first plugin.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {mergedPlugins.map((plugin) => (
              <Card key={plugin.pluginId} className={`transition-all ${
                plugin.active ? 'border-green-200 bg-green-50' : 
                plugin.isLoaded ? 'border-blue-200 bg-blue-50' : 
                'border-gray-200'
              }`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        plugin.active ? 'bg-green-500' : 
                        plugin.isLoaded ? 'bg-blue-500' : 
                        'bg-gray-400'
                      }`} />
                      <div>
                        <CardTitle className="text-lg">{plugin.name}</CardTitle>
                        <CardDescription>
                          v{plugin.version}
                          {plugin.active && ' • Active'}
                          {plugin.isLoaded && !plugin.active && ' • Loaded'}
                          {plugin.createdAt && ` • ${plugin.createdAt.toLocaleDateString()}`}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={plugin.active}
                          onCheckedChange={() => handleTogglePlugin(plugin)}
                          className={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                        {isLoading && (
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedPlugin(plugin)}
                        disabled={isLoading}
                      >
                        <Settings />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeletePlugin(plugin)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {plugin.description && (
                  <CardContent>
                    <p className="text-sm text-gray-600">{plugin.description}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Plugin Details Dialog */}
      {selectedPlugin && (
        <Dialog open={!!selectedPlugin} onOpenChange={() => setSelectedPlugin(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-base">Plugin Details</DialogTitle>
              <DialogDescription className="text-sm">
                {selectedPlugin?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Plugin ID</label>
                <Input value={selectedPlugin?.pluginId || ''} disabled className="text-xs h-8" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Version</label>
                <Input value={selectedPlugin?.version || ''} disabled className="text-xs h-8" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Status</label>
                <div className="flex items-center space-x-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    selectedPlugin?.active ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  <span className="text-xs">
                    {selectedPlugin?.active ? 'Active' : 'Inactive'}
                    {selectedPlugin?.isLoaded && ' (Loaded)'}
                  </span>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setSelectedPlugin(null)} size="sm">
                Close
              </Button>
              <Button onClick={() => selectedPlugin && handleTogglePlugin(selectedPlugin)} size="sm">
                {selectedPlugin?.active ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
